import { InsuranceResponse } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/api-service/v1/insurance_verification_api.pb';
import { InsuranceType } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/shared/enums.pb';
import {
  DentalCoverageRequest,
  InsuranceStatus,
  PayerDetail,
} from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v1/insurance_verification_adapter.pb';
import {
  Coverage,
  VerificationMetricsResponse,
} from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v1/insurance_verification_adapter.pb';
import { isEmpty } from 'lodash-es';
import { formatDate } from '@frontend/date';
import { RelationshipEnum } from '../constants';
import { TuuthFairyDentalSummaryResponse } from '../tuuthfairy-types';
import { InsuranceDetails, UndefinedOrNull, VerificationReportData } from '../types';
import {
  formatDateForDisplay,
  getHighestSpentRecords,
  getHoursSavedByInsuranceVerification,
  getPayerList,
  groupTuuthFairyServicesDataByDate,
  removeNonAlphaNumericCharacters,
  sanitizeText,
} from '../utils';

export const preparePracticeTypeList = (practiceTypeObject: Record<string, string>) => {
  if (isEmpty(practiceTypeObject)) return [];
  return Object.entries(practiceTypeObject).map(([key, value]) => ({ id: key, label: value }));
};

export const prepareInsuranceDetails = (
  details: InsuranceResponse,
  payerList: Record<'label' | 'value', string>[] = [],
  insuranceType: InsuranceType
): InsuranceDetails => {
  if (insuranceType === InsuranceType.INSURANCE_TYPE_SECONDARY) {
    return getSecondaryInsuranceDetails(details);
  } else {
    return getPrimaryInsuranceDetails(details, payerList);
  }
};

export const prepareVerificationReportData = (response?: VerificationMetricsResponse): VerificationReportData => {
  if (!response)
    return {
      totalCount: 0,
      automatedCount: 0,
      manualCount: 0,
      hoursSaved: 0,
    };

  const totalCount = Number(response.overallCount) || 0;
  const automatedCount = Number(response.automatedCount) || 0;
  const manualCount = Number(response.manualCount) || 0;
  const hoursSaved = getHoursSavedByInsuranceVerification(totalCount);

  return { totalCount, automatedCount, manualCount, hoursSaved };
};

export const prepareInsuranceDetailsForDental = (
  insuranceDetails: UndefinedOrNull<InsuranceResponse>,
  payerListResponse: UndefinedOrNull<PayerDetail[]>,
  isDental: boolean,
  coverage: UndefinedOrNull<Coverage>,
  insuranceType: InsuranceType
): InsuranceDetails | null => {
  if (!insuranceDetails || !isDental) {
    return null;
  }
  //This function call prepares insuranceDetails data based on the insurance type(primary or secondary)
  const insuranceDetailsData = prepareInsuranceTypeData(insuranceDetails, insuranceType);
  const payerList = getPayerList(payerListResponse);
  if (coverage) {
    insuranceDetailsData.coverage = coverage || {};
  }
  let payerId = '';
  if (insuranceDetailsData?.payerCompanyId) {
    const payer = payerList.find((payer) => payer.value === insuranceDetailsData.payerCompanyId);
    payerId = payer?.label ? insuranceDetailsData.payerCompanyId : '';
  }

  if (!payerId && insuranceDetailsData?.onederfulPayerId) {
    const payer = payerList.find((payer) => payer.value === insuranceDetailsData.onederfulPayerId);
    payerId = payer?.label ? insuranceDetailsData.onederfulPayerId : '';
  }
  const overriddenCoverageCreatedDate = insuranceDetailsData?.coverage?.overriddenCoverage?.createdAt;
  const thirdPartyCoverageCreatedDate = insuranceDetailsData.coverage?.thirdPartyCoverage?.lastVerifiedAt || '';
  const shouldShowOverriddenCoverage =
    overriddenCoverageCreatedDate &&
    (!thirdPartyCoverageCreatedDate ||
      new Date(overriddenCoverageCreatedDate) > new Date(thirdPartyCoverageCreatedDate));

  if (shouldShowOverriddenCoverage) {
    const pmsRelationShip = insuranceDetailsData?.relationship ?? RelationshipEnum.Self;

    return {
      onderfulPayerId: insuranceDetailsData?.onederfulPayerId ?? '',
      payerId: payerId || insuranceDetailsData?.payerCompanyId || '',
      pmsPayerId: insuranceDetailsData?.payerCompanyId || '',
      payerName: insuranceDetailsData?.payerCompanyName || '',
      groupName: removeNonAlphaNumericCharacters(insuranceDetailsData?.groupName) ?? '',
      groupNumber: removeNonAlphaNumericCharacters(insuranceDetailsData?.groupNumber) ?? '',

      nationalProviderId: removeNonAlphaNumericCharacters(insuranceDetailsData?.nationalProviderId) ?? '',
      providerTaxId: removeNonAlphaNumericCharacters(insuranceDetailsData?.providerTaxId) ?? '',

      firstName: sanitizeText(insuranceDetailsData?.firstName) ?? '',
      lastName: sanitizeText(insuranceDetailsData?.lastName) ?? '',
      dateOfBirth: formatDateForDisplay(insuranceDetailsData?.birthDate || ''),
      memberId: removeNonAlphaNumericCharacters(insuranceDetailsData?.memberId || ''),
      relationship: (pmsRelationShip === 'Self' ? 'se' : pmsRelationShip) as RelationshipEnum,
      coverageStatus: insuranceDetailsData.coverage?.overriddenCoverage?.coverage || '',
      shouldShowOverriddenStatus: true,
    };
  }
  const subscriberInfo = (insuranceDetailsData.coverage?.thirdPartyCoverage?.details as DentalCoverageRequest)
    ?.subscriber;

  const insuranceRequestDetails = insuranceDetailsData?.coverage?.thirdPartyCoverage?.insuranceRequestDetails;
  const relationship =
    (insuranceRequestDetails?.relationship || insuranceDetailsData?.relationship) ?? RelationshipEnum.Self;

  const tuuthfairyDentalSummaryInfo = (
    insuranceDetailsData?.coverage?.thirdPartyCoverage as TuuthFairyDentalSummaryResponse
  )?.dentalSummaryInfo;
  //History data is available in services
  const servicesData = tuuthfairyDentalSummaryInfo?.plan?.services;
  const maximumBenefits = tuuthfairyDentalSummaryInfo?.plan?.maximums;
  const deductibleBenefits = tuuthfairyDentalSummaryInfo?.plan?.deductibles;
  const maximum = getHighestSpentRecords(maximumBenefits);
  const deductible = getHighestSpentRecords(deductibleBenefits);
  const groupServicesDataByDate = groupTuuthFairyServicesDataByDate(servicesData);
  const endDate = insuranceDetailsData?.coverage?.thirdPartyCoverage?.dentalSummaryInfo?.plan?.endDate;

  return {
    payerId: insuranceRequestDetails?.payerId || payerId || '',
    onderfulPayerId: insuranceDetailsData.onederfulPayerId ?? '',
    pmsId: insuranceDetailsData.pmsId ?? '',
    payerName: insuranceRequestDetails?.payerName || insuranceDetailsData?.payerCompanyName || '',
    groupName:
      removeNonAlphaNumericCharacters(insuranceRequestDetails?.groupName || insuranceDetailsData?.groupName) ?? '',
    groupNumber:
      removeNonAlphaNumericCharacters(insuranceRequestDetails?.groupName || insuranceDetailsData?.groupNumber) ?? '',

    nationalProviderId:
      removeNonAlphaNumericCharacters(
        insuranceRequestDetails?.nationalProviderId || insuranceDetailsData?.nationalProviderId
      ) ?? '',
    providerTaxId:
      removeNonAlphaNumericCharacters(insuranceRequestDetails?.providerTaxId || insuranceDetailsData?.providerTaxId) ??
      '',

    firstName: sanitizeText(insuranceRequestDetails?.firstName || insuranceDetailsData?.firstName) ?? '',
    lastName: sanitizeText(insuranceRequestDetails?.lastName || insuranceDetailsData?.lastName) ?? '',
    dateOfBirth: formatDateForDisplay(insuranceRequestDetails?.birthDate || insuranceDetailsData.birthDate || ''),
    memberId: removeNonAlphaNumericCharacters(
      insuranceRequestDetails?.memberId || insuranceDetailsData?.memberId || ''
    ),

    relationship: (relationship === 'Self' ? 'se' : relationship) as RelationshipEnum,
    subscriberId: '',
    subscriberFirstName: insuranceRequestDetails?.subscriberFirstName || subscriberInfo?.firstName || '',
    subscriberLastName: insuranceRequestDetails?.subscriberLastName || subscriberInfo?.lastName || '',
    subscriberDateOfBirth: insuranceRequestDetails?.subscriberDateOfBirth || subscriberInfo?.dateOfBirth || '',
    subscriberMemberId: insuranceRequestDetails?.subscriberMemberId || subscriberInfo?.memberId || '',

    effectiveDate: formatDate(insuranceDetailsData.coverage?.thirdPartyCoverage?.coverage?.effectiveDate, 'MM/DD/YYYY'),
    // SP-TODO: remove this casting once BE update schema type
    thirdPartyCoverageStatus:
      (insuranceDetailsData.coverage?.thirdPartyCoverage?.coverage?.status as InsuranceStatus) ??
      InsuranceStatus.UNKNOWN,
    lastVerifiedAt: insuranceDetailsData.coverage?.thirdPartyCoverage?.lastVerifiedAt || '',
    coverageStatus: (insuranceDetailsData.coverage?.thirdPartyCoverage?.coverage?.status as InsuranceStatus) || '',
    overrideCoverageStatus: insuranceDetailsData.coverage?.overriddenCoverage?.coverage,
    verificationStatus: insuranceDetailsData?.coverage?.thirdPartyCoverage?.verificationStatus,
    historyData: groupServicesDataByDate,
    isElegibleForWebsiteResp: (insuranceDetailsData?.coverage?.thirdPartyCoverage as TuuthFairyDentalSummaryResponse)
      ?.isEligibleForWebsiteResp,
    benefitLimits: {
      maximum,
      deductible,
    },
    endDate,
    pmsPayerId:
      insuranceDetailsData?.payerCompanyId ||
      insuranceDetailsData?.coverage?.thirdPartyCoverage?.insuranceRequestDetails?.pmsPayerId ||
      '',
    errorDetails: insuranceDetailsData?.coverage?.thirdPartyCoverage?.errorDetails,
  };
};

export const prepareInsuranceDetailsForNonDental = (
  insuranceDetails: InsuranceResponse,
  payerListResponse: UndefinedOrNull<PayerDetail[]>,
  isWellness: boolean
): InsuranceDetails | null => {
  if (!insuranceDetails || !isWellness) return null;
  const payerList = getPayerList(payerListResponse);
  if (isEmpty(insuranceDetails)) return {} as InsuranceDetails;

  let payerId = '';
  const requestPayerID = insuranceDetails?.coverage?.thirdPartyCoverage?.insuranceRequestDetails?.payerId;
  if (requestPayerID) {
    const payer = payerList.find((payer) => payer.value === requestPayerID);
    payerId = payer?.label ? requestPayerID : '';
  }

  const {
    payerName,
    groupName,
    groupNumber,
    nationalProviderId,
    providerTaxId,
    firstName,
    lastName,
    birthDate,
    memberId,
    relationship,
    subscriberFirstName,
    subscriberLastName,
    subscriberDateOfBirth,
    subscriberMemberId,
    pmsPayerId,
  } = insuranceDetails?.coverage?.thirdPartyCoverage?.insuranceRequestDetails || {};

  const { effectiveDate, status } = insuranceDetails?.coverage?.thirdPartyCoverage?.coverage || {};

  return {
    payerId,
    payerName: payerName || '',
    groupName: removeNonAlphaNumericCharacters(groupName) || '',
    groupNumber: removeNonAlphaNumericCharacters(groupNumber || ''),

    nationalProviderId: removeNonAlphaNumericCharacters(nationalProviderId) || '',
    providerTaxId: removeNonAlphaNumericCharacters(providerTaxId) || '',

    firstName: sanitizeText(firstName) || '',
    lastName: sanitizeText(lastName) || '',
    dateOfBirth: formatDate(birthDate, 'MM/DD/YYYY', true),
    memberId: removeNonAlphaNumericCharacters(memberId || ''),

    relationship: (relationship as RelationshipEnum) || RelationshipEnum.Self,
    subscriberId: '',
    subscriberFirstName: subscriberFirstName ?? '',
    subscriberLastName: subscriberLastName ?? '',
    subscriberDateOfBirth: subscriberDateOfBirth ?? '',
    subscriberMemberId: subscriberMemberId ?? '',
    overrideCoverageStatus: insuranceDetails.coverage?.overriddenCoverage?.coverage,
    effectiveDate: effectiveDate ? formatDate(effectiveDate, 'MM/DD/YYYY') : '',
    thirdPartyCoverageStatus: (status as InsuranceStatus) ?? InsuranceStatus.UNKNOWN,
    lastVerifiedAt: insuranceDetails?.coverage?.thirdPartyCoverage?.lastVerifiedAt || '',
    coverageStatus: insuranceDetails?.coverage?.thirdPartyCoverage?.coverage?.status || '',
    pmsPayerId,
  };
};
//This function prepares insurance details based on the insurance type (primary or secondary)
export const prepareInsuranceTypeData = (
  insuranceDetailsResponse: UndefinedOrNull<InsuranceResponse>,
  insuranceType: InsuranceType
) => {
  if (insuranceType === InsuranceType.INSURANCE_TYPE_SECONDARY && insuranceDetailsResponse) {
    return {
      ...insuranceDetailsResponse,
      payerCompanyId: insuranceDetailsResponse.secondaryCompanyId,
      payerCompanyName: insuranceDetailsResponse.secondaryCompanyName,
      groupName: insuranceDetailsResponse.secondaryGroupName,
      groupNumber: insuranceDetailsResponse.secondaryGroupNumber,
      memberId: insuranceDetailsResponse.secondaryMemberId,
      nationalProviderId: insuranceDetailsResponse.secondaryNationalProviderId,
      providerTaxId: insuranceDetailsResponse.secondaryProviderTaxId,
      coverage: {}, //as insuranceDetailsResponse only contains primary coverage details making it empty for secondary insurance,
      relationship: RelationshipEnum.Self, // as insuranceDetailsResponse does not contain relationship for secondary insurance defaulting it to self
    };
  } else {
    return { ...(insuranceDetailsResponse || {}) };
  }
};

export const getSecondaryInsuranceDetails = (details: InsuranceResponse): InsuranceDetails => {
  return {
    payerId: details?.secondaryCompanyId || '',
    payerName: details?.secondaryCompanyName || '',
    groupName: removeNonAlphaNumericCharacters(details?.secondaryGroupName) || '',
    groupNumber: removeNonAlphaNumericCharacters(details?.secondaryGroupNumber || ''),
    memberId: removeNonAlphaNumericCharacters(details?.secondaryMemberId || ''),
    nationalProviderId: removeNonAlphaNumericCharacters(details?.secondaryNationalProviderId) || '',
    providerTaxId: removeNonAlphaNumericCharacters(details?.secondaryProviderTaxId) || '',
    relationship: RelationshipEnum.Self, // defaulting to self
    firstName: sanitizeText(details?.firstName) ?? '',
    lastName: sanitizeText(details?.lastName) ?? '',
    dateOfBirth: formatDate(details?.birthDate, 'MM/DD/YYYY', true),
    pmsId: details.pmsId ?? '',
    pmsPayerId: details?.payerCompanyId || '',
    coverageStatus: InsuranceStatus.UNKNOWN, // secondary coverage not in response
    zipCode: details?.zipCode || '',
  };
};

export const getPrimaryInsuranceDetails = (
  details: InsuranceResponse,
  payerList: Record<'label' | 'value', string>[] = []
): InsuranceDetails => {
  let payerId = '';

  if (details?.payerCompanyId) {
    const payer = payerList.find((payer) => payer.value === details.payerCompanyId);
    payerId = payer?.label ? details.payerCompanyId : '';
  }

  if (!payerId && details?.onederfulPayerId) {
    const payer = payerList.find((payer) => payer.value === details.onederfulPayerId);
    payerId = payer?.label ? details.onederfulPayerId : '';
  }

  const subscriberInfo = (details.coverage?.thirdPartyCoverage?.details as DentalCoverageRequest)?.subscriber;
  const insuranceRequestDetails = details?.coverage?.thirdPartyCoverage?.insuranceRequestDetails;
  const relationship = (insuranceRequestDetails?.relationship || details?.relationship) ?? RelationshipEnum.Self;

  return {
    payerId: details.payerCompanyId || insuranceRequestDetails?.payerId || payerId || '',
    onderfulPayerId: details.onederfulPayerId ?? '',
    pmsId: details.pmsId ?? '',
    payerName: insuranceRequestDetails?.payerName || details?.payerCompanyName || '',
    groupName: removeNonAlphaNumericCharacters(insuranceRequestDetails?.groupName || details?.groupName) ?? '',
    groupNumber: removeNonAlphaNumericCharacters(insuranceRequestDetails?.groupNumber || details?.groupNumber) ?? '',
    nationalProviderId:
      removeNonAlphaNumericCharacters(insuranceRequestDetails?.nationalProviderId || details?.nationalProviderId) ?? '',
    providerTaxId:
      removeNonAlphaNumericCharacters(insuranceRequestDetails?.providerTaxId || details?.providerTaxId) ?? '',
    firstName: sanitizeText(insuranceRequestDetails?.firstName || details?.firstName) ?? '',
    lastName: sanitizeText(insuranceRequestDetails?.lastName || details?.lastName) ?? '',
    dateOfBirth: formatDate(insuranceRequestDetails?.birthDate || details.birthDate, 'MM/DD/YYYY', true),
    memberId: removeNonAlphaNumericCharacters(insuranceRequestDetails?.memberId || details?.memberId || ''),
    relationship: (relationship === 'Self' ? 'Self' : relationship) as RelationshipEnum,
    subscriberId: '',
    subscriberFirstName: insuranceRequestDetails?.subscriberFirstName || subscriberInfo?.firstName || '',
    subscriberLastName: insuranceRequestDetails?.subscriberLastName || subscriberInfo?.lastName || '',
    subscriberDateOfBirth: insuranceRequestDetails?.subscriberDateOfBirth || subscriberInfo?.dateOfBirth || '',
    subscriberMemberId: insuranceRequestDetails?.subscriberMemberId || subscriberInfo?.memberId || '',
    effectiveDate: formatDate(details.coverage?.thirdPartyCoverage?.coverage?.effectiveDate, 'MM/DD/YYYY'),
    thirdPartyCoverageStatus:
      (details.coverage?.thirdPartyCoverage?.coverage?.status as InsuranceStatus) ?? InsuranceStatus.UNKNOWN,
    lastVerifiedAt: details.coverage?.thirdPartyCoverage?.lastVerifiedAt || '',
    coverageStatus: details?.coverage?.thirdPartyCoverage?.coverage?.status || '',
    pmsPayerId:
      details?.payerCompanyId || details?.coverage?.thirdPartyCoverage?.insuranceRequestDetails?.pmsPayerId || '',
    zipCode: details?.zipCode || '',
  };
};
